import React, { useContext, useEffect, useState } from 'react';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { ROLES } from '../../components/misc/Utils';

// Simple admin UI for uploading and deleting presentation music
// Usage: import PresentationMusicAdmin and render where appropriate (Admin pages)

const PresentationMusicAdmin = ({ competition = 'global', allowedRoles = [ROLES.ADMIN, ROLES.SCORECOMMITTEE] }) => {
  const { firebase } = useContext(FirebaseNewContext) || {};
  const [files, setFiles] = useState([]); // [{name, path, url}]
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    console.log('PresentationMusicAdmin firebase keys:', firebase ? Object.keys(firebase).slice(0,50) : 'no firebase');
    console.log('uploadPresentationMusic typeof:', firebase ? typeof firebase.uploadPresentationMusic : 'undefined');

    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        if (!firebase || typeof firebase.getPresentationMusicList !== 'function') {
          setFiles([]);
          setLoading(false);
          return;
        }
        const list = await firebase.getPresentationMusicList(competition);
        if (!mounted) return;
        setFiles(list || []);
      } catch (e) {
        console.error('Error loading presentation music list', e);
        setFiles([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [firebase, competition]);

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setSelectedFile(f || null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage('Choose a file first');
      return;
    }
    if (!firebase || typeof firebase.uploadPresentationMusic !== 'function') {
      setMessage('Firebase upload not available');
      return;
    }
    setUploading(true);
    setMessage('');
    try {
      const res = await firebase.uploadPresentationMusic(selectedFile, competition);
      setFiles(prev => [...prev, res]);
      setSelectedFile(null);
      setMessage(`Uploaded ${res.name}`);
    } catch (e) {
      console.error('Upload failed', e);
      setMessage('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    if (!firebase || typeof firebase.deletePresentationMusic !== 'function') {
      setMessage('Firebase delete not available');
      return;
    }
    try {
      const ok = await firebase.deletePresentationMusic(name, competition);
      if (ok) {
        setFiles(prev => prev.filter(f => f.name !== name));
        setMessage(`Deleted ${name}`);
      } else {
        setMessage('Delete failed');
      }
    } catch (e) {
      console.error('Delete error', e);
      setMessage('Delete failed');
    }
  };

  return (
    <div style={{ padding: 12, border: '1px solid #ddd', borderRadius: 6, maxWidth: 720 }}>
      <h3>Presentation Music Admin</h3>
      <p style={{ marginTop: 0 }}>Upload or remove songs used for slide presentations. Files are stored in <code>presentation-music/{competition}</code>.</p>

      <div style={{ marginBottom: 8 }}>
        <input type="file" accept="audio/*" onChange={onFileChange} />
        <button onClick={handleUpload} disabled={uploading || !selectedFile} style={{ marginLeft: 8 }}>{uploading ? 'Uploading...' : 'Upload'}</button>
      </div>
      {message ? <div style={{ marginBottom: 8 }}>{message}</div> : null}

      <div>
        <h4>Uploaded songs</h4>
        {loading ? <div>Loading...</div> : (
          files.length === 0 ? <div>No songs uploaded.</div> : (
            <ul>
              {files.map(f => (
                <li key={f.name} style={{ marginBottom: 6 }}>
                  <a href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
                  <button onClick={() => handleDelete(f.name)} style={{ marginLeft: 8 }}>Delete</button>
                </li>
              ))}
            </ul>
          )
        )}

      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
        Note: The player will use these files from Firebase when available and fall back to local bundled songs otherwise.
      </div>
    </div>
  );
};

export default PresentationMusicAdmin;
